import React, {useCallback, useState} from "react";
import "./index.scss";
import {connect} from "react-redux";
import {LoggedInLayout} from "features/layouts/pages/logged-in";
import {TextField} from "features/components/text-field";
import {__} from "services/i18n";
import {Icon} from "features/components/icon";
import Select from "features/components/select";
import {
    videoSearchData, videoSearchErrorSelector,
    videoSearchFilters, videoSearchHasNext,
    videoSearchLoading,
    videoSearchNextLoading
} from "features/pages/videos/selectors";
import {setVideoSearchFilters, startVideosSearch, videoSearchFetchNextPage} from "features/pages/videos/reducer";
import {LoadingLogoPortal} from "features/components/loading-logo";
import Title from "features/components/title";
import {HorizontalScrollSection} from "features/components/horizontal-scroll-section";
import VideoCard from "features/components/video-card";
import {DirectoryCard} from "features/components/directory-card";
import Button from "features/components/button";
import Text from "features/components/text";
import {withToastManager} from "react-toast-notifications";
import {useMediaQuery} from "services/utils/utils";
import {MOBILE_MEDIA_QUERY_THRESHOLD, VIDEO_TYPES, VIDEOS_ORDER_BY} from "constants.js";
import {staticDataInterests} from "store/reducer/static/selectors";

/* istanbul ignore next */
const mapStateToProps = state => ({
    filters: videoSearchFilters(state),
    data: videoSearchData(state),
    loading: videoSearchLoading(state),
    next_loading: videoSearchNextLoading(state),
    has_next_page: videoSearchHasNext(state),
    error: videoSearchErrorSelector(state),
    categories: staticDataInterests(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    setVideoSearchFilters,
    videoSearchFetchNextPage,
    startVideosSearch
};

export const VideosPage = (props) => {

    const {filters, data, loading, next_loading, has_next_page, error, setVideoSearchFilters, videoSearchFetchNextPage, startVideosSearch, categories} = props;

    React.useEffect(() => {
        startVideosSearch();
    }, [startVideosSearch]);

    const setFilterValue = useCallback((key, value) => {
        setVideoSearchFilters({
            [key]: value
        })
    }, [setVideoSearchFilters]);

    React.useEffect(() => {
        if(!error.inError) return;
        props.toastManager.add(__(error.error), { appearance: "error", id: "video-search-failure-toast" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_THRESHOLD);
    const [openFilters, setOpenFilters] = useState(false);
    const toggleOpenFilters = useCallback(() => {
        setOpenFilters(!openFilters);
    }, [openFilters, setOpenFilters]);
    const filtersRow = useCallback(() => {
        return <div className="row filters">
            <div className={"search"}>
                <Icon>search</Icon>
                <TextField type={"text"} value={filters.search} onChange={(val) => setFilterValue("search", val)} placeholder={__("videos.filters.search")} />
                {!!filters.search && <div className="reset" onClick={() => setFilterValue("search", "")}><Icon>close</Icon></div>}
            </div>
            <div className={`choices ${openFilters ? "mobile-show" : "mobile-hide"}`}>
                <div className="left">
                    <Select
                        value={filters.categories}
                        onChange={(val) => setFilterValue("categories", val)}
                        multiple={true}
                        values={categories}
                        placeholder={__("videos.filters.categories")}
                        noSelectionString={__("common.all.male")}
                    />
                    <Select
                        value={filters.type}
                        onChange={(val) => setFilterValue("type", val)}
                        multiple={false}
                        values={VIDEO_TYPES}
                        placeholder={__("videos.filters.type")}
                        noSelectionString={__("common.all.male")}
                    />
                </div>
                <Select
                    value={filters.order_by}
                    onChange={(val) => setFilterValue("order_by", val)}
                    values={VIDEOS_ORDER_BY}
                    placeholder={__("common.order-by.placeholder")}
                    noSelectionString={__("videos.filters.order-by.default")}
                />
            </div>
            {isMobile && <div className="show-filters">
                <Button title={__("buttons.show-filters")} onClick={toggleOpenFilters} type={"clear"} icon={openFilters ? "arrow-dropdown-up" : "arrow-dropdown-down"} />
            </div>}
        </div>
    }, [filters.categories, filters.order_by, filters.search, filters.type, isMobile, openFilters, setFilterValue, toggleOpenFilters, categories]);

    const favoriteSection = useCallback(() => {
        const videos = data.favorite;
        if(!videos.length) return null;
        return <div className="home-section">
            <Title size={34}>{__("videos.titles.favorite")}</Title>
            <HorizontalScrollSection type={"light"}>
                {
                    videos.map(item => <VideoCard key={item.id} video={item} type={"light"} />)
                }
            </HorizontalScrollSection>
        </div>
    }, [data.favorite]);
    const directorySection = useCallback(() => {
        const directories = data.directory;
        if(!directories.length) return null;
        return <div className="home-section">
            <Title size={34}>{__("videos.titles.directories")}</Title>
            <HorizontalScrollSection type={"light"}>
                {
                    directories.map(item => <DirectoryCard key={item.id} directory={item} />)
                }
            </HorizontalScrollSection>
        </div>
    }, [data.directory]);

    const allVideoSection = useCallback(() => {
        const videos = data.video;
        return <div className="home-section">
            <Title size={34}>{__("videos.titles.all")}</Title>
            {
                !videos.length ?
                    <Text size={16} align={"center"}>{__("videos.no-results")}</Text> :
                    <>
                        <div className="all-videos">
                            {
                                videos.map(item => <VideoCard key={item.id} video={item} type={"light"}/>)
                            }
                        </div>
                        {has_next_page && <div className="row next">
                            <Button title={__("buttons.load-more")}
                                    onClick={videoSearchFetchNextPage}
                                    type={"outlined"}
                                    status={next_loading ? "loading" : null}
                            />
                        </div>}
                    </>
            }
        </div>
    }, [data.video, has_next_page, next_loading, videoSearchFetchNextPage]);

    return <LoggedInLayout type={"light"}
                           className={"videos-page"}
                           breadcrumbs={[{title: "common.home-breadcrumb", link:"/"}, {title: "videos.breadcrumb"}]}
    >
        {loading && <LoadingLogoPortal />}
        {filtersRow()}
        {favoriteSection()}
        {directorySection()}
        {allVideoSection()}
    </LoggedInLayout>
};

export default connect(mapStateToProps, mapDispatchToProps)(withToastManager(VideosPage));